/**
 * SaldoCard — Card riepilogativa saldo di un conto bancario.
 *
 * Mostra nome, IBAN mascherato, saldi contabile e disponibile,
 * timestamp dell'ultimo sync e badge dello stato connessione.
 * Avvisa visivamente se il consenso PSD2 è in scadenza entro 14 giorni.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import React from 'react';
import { AlertTriangle, RefreshCw, Wallet } from 'lucide-react';
import { Badge, Card, CardContent } from '@floraltech/ui';
import { useFormatters } from '@lib/formatters';

// Soglia in millisecondi entro cui il consenso è considerato "in scadenza"
const CONSENSO_SCADENZA_THRESHOLD_MS = 14 * 24 * 60 * 60 * 1000;

/** Props per la SaldoCard. */
export interface SaldoCardProps {
  /** UUID del conto bancario. */
  id: string;
  /** Nome descrittivo del conto. */
  nome: string;
  /** IBAN del conto (verrà mascherato). */
  iban: string;
  /** Saldo contabile corrente, null se non ancora sincronizzato. */
  saldo_contabile: number | null;
  /** Saldo disponibile corrente, null se non disponibile. */
  saldo_disponibile: number | null;
  /** Codice valuta ISO 4217 (es. "EUR"). */
  valuta: string;
  /** Stato connessione PSD2: "attiva" | "scaduta" | "errore" | "in_attesa". */
  stato_connessione: string;
  /** Timestamp ISO dell'ultima sincronizzazione movimenti, null se mai eseguita. */
  ultimo_sync: string | null;
  /** Data ISO di scadenza del consenso PSD2, null se non disponibile. */
  consenso_scadenza: string | null;
  /** Callback invocato al click sulla card (navigazione al dettaglio). */
  onClick?: () => void;
}

/** Mappa stato connessione → variante Badge e label. */
const STATO_BADGE: Record<
  string,
  { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }
> = {
  attiva: { variant: 'default', label: 'Attiva' },
  scaduta: { variant: 'destructive', label: 'Scaduta' },
  errore: { variant: 'destructive', label: 'Errore' },
  in_attesa: { variant: 'secondary', label: 'In attesa' },
};

/**
 * Maschera un IBAN mostrando solo i primi 4 e gli ultimi 4 caratteri.
 *
 * @param iban - IBAN completo da mascherare.
 * @returns IBAN mascherato (es. "IT60 •••• •••• 1234").
 */
function maskIban(iban: string): string {
  if (iban.length <= 8) return iban;
  return `${iban.slice(0, 4)} •••• •••• ${iban.slice(-4)}`;
}

/**
 * Determina se il consenso PSD2 è in scadenza entro la soglia configurata.
 *
 * @param consenso_scadenza - Stringa ISO della data di scadenza, null se assente.
 * @returns true se la scadenza è entro CONSENSO_SCADENZA_THRESHOLD_MS.
 */
function isConsensoExpiring(consenso_scadenza: string | null): boolean {
  if (!consenso_scadenza) return false;
  return (
    new Date(consenso_scadenza).getTime() - Date.now() < CONSENSO_SCADENZA_THRESHOLD_MS
  );
}

/**
 * Card riepilogativa di un conto bancario collegato.
 *
 * @param props - SaldoCardProps con i dati del conto.
 * @returns Card cliccabile con saldi, stato e info consenso.
 */
export const SaldoCard: React.FC<SaldoCardProps> = (props) => {
  const fmt = useFormatters();

  const badge = STATO_BADGE[props.stato_connessione] ?? STATO_BADGE['in_attesa'];
  const consentoExpiring = isConsensoExpiring(props.consenso_scadenza);

  return (
    <Card
      className="cursor-pointer hover:border-primary/30 transition-colors"
      onClick={props.onClick}
    >
      <CardContent className="p-4">
        {/* Header: nome conto + badge stato */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Wallet className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{props.nome}</span>
          </div>
          <Badge variant={badge.variant}>{badge.label}</Badge>
        </div>

        {/* IBAN mascherato */}
        <div className="text-xs text-muted-foreground mt-1 font-mono">
          {maskIban(props.iban)}
        </div>

        {/* Saldo contabile */}
        <div className="mt-3">
          <div className="text-xs text-muted-foreground uppercase tracking-wide">
            Saldo contabile
          </div>
          <div className="text-2xl font-semibold mt-0.5 tabular-nums">
            {props.saldo_contabile != null
              ? fmt.currency(props.saldo_contabile)
              : '—'}
          </div>
        </div>

        {/* Saldo disponibile (solo se presente) */}
        {props.saldo_disponibile != null && (
          <div className="mt-2">
            <div className="text-xs text-muted-foreground">
              Disponibile:{' '}
              <span className="tabular-nums">
                {fmt.currency(props.saldo_disponibile)}
              </span>
            </div>
          </div>
        )}

        {/* Footer: ultimo sync + avviso consenso */}
        <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <RefreshCw className="h-3 w-3" />
            {props.ultimo_sync
              ? fmt.dateTime(props.ultimo_sync)
              : 'Mai sincronizzato'}
          </div>

          {consentoExpiring && (
            <div className="flex items-center gap-1 text-yellow-600">
              <AlertTriangle className="h-3 w-3" />
              Consenso in scadenza
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SaldoCard;
